import { z } from 'zod';
import {
  ApiErrorBodySchema,
  AuthTokensSchema,
  BienDtoSchema,
  CurrentUserSchema,
  DossierDtoSchema,
  ElementDtoSchema,
  MandantDtoSchema,
  NiveauDtoSchema,
  QuantityDtoSchema,
  QuantityFormulaDtoSchema,
  QuantityPreviewSchema,
  StructureDtoSchema,
  TransitionDtoSchema,
  BulkAssignmentResultDtoSchema,
  CatalogVersionDtoSchema,
  CoefficientResolutionDtoSchema,
  CoefficientSetDtoSchema,
  CoefficientTypeDtoSchema,
  CoefficientVersionDtoSchema,
  CostCatalogDtoSchema,
  CostResolutionPreviewDtoSchema,
  CostSnapshotDtoSchema,
  CalcRunDtoSchema,
  CalcRunListDtoSchema,
  MethodRunDtoSchema,
  DocumentDtoSchema,
  PvVariantsDtoSchema,
  type PvVariant,
  type PvVariantsDto,
  MarketDataDtoSchema,
  DocumentListDtoSchema,
  PortalDossierSummarySchema,
  PortalDossierDetailSchema,
  type PortalDossierSummary,
  type PortalDossierDetail,
  DashboardSummarySchema,
  type DashboardSummary,
  DocumentManifestDtoSchema,
  GenerationRunDtoSchema,
  PipelineStatusDtoSchema,
  StartPipelineDtoSchema,
  TemplateDtoSchema,
  DuplicationApplyResultDtoSchema,
  DuplicationPreviewDtoSchema,
  GeoEntityDtoSchema,
  ImportReportDtoSchema,
  MeasurementUnitRefDtoSchema,
  TariffGroupDtoSchema,
  TechnicalCategoryDtoSchema,
  UnitCostDtoSchema,
  ComponentTypeDtoSchema,
  pageSchema,
  type AuthTokens,
  type BienDto,
  type BienType,
  type CurrentUser,
  type DossierDto,
  type DossierScenario,
  type DossierStatut,
  type ElementDto,
  type MandantCoordonnees,
  type MandantDto,
  type MandantType,
  type MeasurementUnit,
  type NiveauDto,
  type Page,
  type QuantityDto,
  type QuantityFormulaCode,
  type QuantityFormulaDto,
  type QuantityInputMode,
  type QuantityPreview,
  type StructureDto,
  type StructureKind,
  type TransitionDto,
  type BulkAssignmentResultDto,
  type CalcMethodCode,
  type CalcRunDto,
  type CalcRunListDto,
  type MethodRunDto,
  type DocumentDto,
  type MarketDataDto,
  type MarketDataSource,
  type DocumentListDto,
  type DocumentManifestDto,
  type DocumentType,
  type GenerationRunDto,
  type PipelineStatusDto,
  type StartPipelineDto,
  type TemplateDto,
  type CatalogVersionDto,
  type CoefficientResolutionDto,
  type CoefficientSetDto,
  type CoefficientTypeDto,
  type CoefficientVersionDto,
  type ComponentTypeDto,
  type CostCatalogDto,
  type CostResolutionPreviewDto,
  type CostSnapshotDto,
  type DuplicationApplyResultDto,
  type DuplicationPreviewDto,
  type GeoEntityDto,
  type GeographicScopeType,
  type ImportReportDto,
  type MeasurementUnitRefDto,
  type TariffGroupDto,
  type TariffVariationType,
  type TechnicalCategoryDto,
  type UnitCostDto,
} from './api-types';
import {
  createTokenHolder,
  tokenHolder as defaultTokenHolder,
  type TokenHolder,
} from './tokenStore';

const API_BASE = '/api/v1';

/** Typed error carrying the API error contract for graceful French rendering. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: string[];
  readonly correlationId?: string;

  constructor(
    status: number,
    body: { code: string; message: string; details?: string[]; correlationId?: string },
  ) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
    this.correlationId = body.correlationId;
  }
}

// --- Request/response payload shapes -----------------------------------------

export interface LoginInput {
  email: string;
  password: string;
  twoFactorCode?: string;
}

export interface MandantListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  type?: MandantType | '';
  includeInactive?: boolean;
}

export interface MandantCreateInput {
  nom: string;
  type: MandantType;
  coordonnees?: MandantCoordonnees;
}

export type MandantUpdateInput = Partial<MandantCreateInput>;

export type DossierSortField = 'createdAt' | 'updatedAt' | 'numero' | 'statut';

export interface DossierListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  statut?: DossierStatut | '';
  mandantId?: string;
  ownerId?: string;
  sort?: `${DossierSortField}:${'asc' | 'desc'}`;
}

export interface DossierCreateInput {
  mandantId: string;
  scenario: DossierScenario;
  objet?: string;
  ownerId?: string;
}

export interface DossierUpdateInput {
  objet?: string;
  scenario?: DossierScenario;
  mandantId?: string;
  ownerId?: string;
  /** LOT 9A — select the PV variant (PV_SEUL only). `null` clears the selection. */
  pvVariant?: PvVariant | null;
}

export interface DossierTransitionInput {
  toStatut: DossierStatut;
  justification?: string;
}

// --- LOT 4 : structure immobilière --------------------------------------------

export interface BienListParams {
  page?: number;
  pageSize?: number;
}

export interface BienCreateInput {
  designation: string;
  type: BienType;
  reference?: string;
  /** Decimal as a string (e.g. "300.5") — passed through untouched. */
  superficie?: string;
  typeZone?: string;
  affectation?: string;
  titreJouissance?: string;
  nomSurTitre?: string;
  ordre?: number;
}

export type BienUpdateInput = Partial<BienCreateInput>;

// --- LOT 8C: market data (REQ-F-061) ------------------------------------------
export interface MarketDataListParams {
  page?: number;
  pageSize?: number;
}

export interface MarketDataCreateInput {
  source: MarketDataSource;
  localisation: string;
  /** Decimals as strings — passed through untouched (server keeps numeric authority). */
  superficieM2?: string;
  prix?: string;
  prixM2?: string;
  dateReleve?: string;
  observation?: string;
  ordre?: number;
}

export type MarketDataUpdateInput = Partial<MarketDataCreateInput>;

export interface StructureCreateInput {
  libelle: string;
  type?: string;
  kind?: StructureKind;
  ordre?: number;
}

export type StructureUpdateInput = Partial<StructureCreateInput>;

export interface NiveauCreateInput {
  libelle: string;
  ordre?: number;
}

export type NiveauUpdateInput = Partial<NiveauCreateInput>;

export interface EspaceElementCreateInput {
  kind: 'ESPACE';
  libelle: string;
  description?: string;
  spaceType?: string;
  ordre?: number;
}

/** Exterior element attached directly to the bien (no niveau). */
export interface BienElementCreateInput {
  kind: 'OUVRAGE' | 'PLUS_VALUE';
  libelle: string;
  description?: string;
  spaceType?: string;
  ordre?: number;
}

export interface ElementUpdateInput {
  libelle?: string;
  description?: string;
  spaceType?: string;
  ordre?: number;
}

export interface QuantityCreateInput {
  inputMode: QuantityInputMode;
  formulaCode?: QuantityFormulaCode;
  parameters?: Record<string, unknown>;
  directQuantity?: string;
  measurementUnit: MeasurementUnit;
  source?: string;
  justification?: string;
}

export interface QuantityPreviewInput {
  inputMode: QuantityInputMode;
  formulaCode?: QuantityFormulaCode;
  parameters?: Record<string, unknown>;
  directQuantity?: string;
  measurementUnit?: MeasurementUnit;
}

// --- LOT 5 : référentiels (DEC-005) --------------------------------------------

/** Wire path segment of a geographic referential collection. */
export type GeoEntityKind = 'countries' | 'cities' | 'districts' | 'zone-categories';

export interface GeoListParams {
  countryId?: string;
  cityId?: string;
  includeInactive?: boolean;
}

export interface GeoCreateInput {
  code: string;
  name: string;
  countryId?: string;
  cityId?: string;
  zoneCategoryId?: string;
}

export type GeoUpdateInput = Partial<GeoCreateInput>;

export interface CodeNameInput {
  code: string;
  name: string;
}

export interface ComponentTypeCreateInput {
  categoryId: string;
  code: string;
  name: string;
}

export interface CatalogVersionCreateInput {
  validFrom: string;
  validTo?: string;
}

/** Lifecycle actions shared by catalog versions and coefficient versions. */
export type ReferentialLifecycleAction = 'submit' | 'approve' | 'reject';

export interface UnitCostListParams {
  scopeType?: GeographicScopeType | '';
  scopeId?: string;
  componentTypeId?: string;
}

export interface UnitCostCreateInput {
  scopeType: GeographicScopeType;
  scopeId: string;
  componentTypeId: string;
  measurementUnitId: string;
  /** Decimal as a string — passed through untouched. */
  amount: string;
  validFrom: string;
  validTo?: string;
  source?: string;
}

export interface UnitCostUpdateInput {
  amount?: string;
  validFrom?: string;
  validTo?: string;
  source?: string;
}

export interface BulkAssignmentInput {
  scopeType: GeographicScopeType;
  scopeId: string;
  componentTypeIds?: string[];
  tariffGroupId?: string;
  measurementUnitId: string;
  amount: string;
  validFrom: string;
  validTo?: string;
  source?: string;
}

export interface DuplicationInput {
  targetVersionId: string;
  source: {
    versionId?: string;
    scopeType?: GeographicScopeType;
    scopeId?: string;
    componentTypeIds?: string[];
  };
  target: {
    scopeType: GeographicScopeType;
    scopeId: string;
  };
  variation: {
    type: TariffVariationType;
    value: string;
  };
}

export interface CoefficientVersionCreateInput {
  validFrom: string;
  validTo?: string;
  source?: string;
}

export interface CoefficientValueInput {
  coefficientTypeId: string;
  /** Decimal (> 0) as a string — validated authoritatively by the server. */
  value: string;
  source?: string;
}

export interface CoefficientResolutionParams {
  setCode: string;
  valuationDate: string;
}

export interface CostResolutionInput {
  valuationDate: string;
  catalogCode: string;
  componentTypeId: string;
  measurementUnitId: string;
  elementId?: string;
}

export interface CostResolutionConfirmInput extends CostResolutionInput {
  /** Explicit, never-silent fallback confirmation (server answers 409 otherwise). */
  confirmFallback?: boolean;
  override?: {
    overrideCost: string;
    justification: string;
  };
}

export interface ImportInput {
  targetVersionId: string;
  format: 'XLSX' | 'CSV';
  fileBase64: string;
}

// --- LOT 6B : calculs (calc runs) ----------------------------------------------

export interface CalcComparisonInput {
  /** Decimal strings — passed through untouched; the server computes everything. */
  superficieM2: string;
  prixFoncierM2: string;
  coefficientSetCode: string;
}

export interface CalcCostInput {
  coefficientSetCode: string;
  batiArrondie?: string;
  parcelleArrondie?: string;
}

export interface CalcCapitalizationInput {
  baseRevenu: string;
  multiplier: string;
  revenuFactors: string[];
  coefficientSetCode?: string;
}

export interface CalcReconstructionLineInput {
  lineRef: string;
  elementId?: string;
  designation: string;
  kind: 'metric' | 'forfait';
  totalGroup: number;
  contributionMode?: 'DIRECT' | 'INCLUDED_IN_PARENT' | 'INFORMATIONAL';
  quantityId?: string;
  directQuantity?: string;
  vetustePct?: string;
  costResolutionSnapshotId?: string;
  forfaitValue?: string;
}

/** The expert's valuation intent. The SERVER resolves/freezes costs, coefficients & rounding. */
export interface CalcPrepareInput {
  valuationDate: string;
  methods: CalcMethodCode[];
  comparison?: CalcComparisonInput;
  cost?: CalcCostInput;
  capitalization?: CalcCapitalizationInput;
  reconstruction?: { lines: CalcReconstructionLineInput[] };
}

export interface CalcAdjustPublishedValueInput {
  publishedValue: string;
  justification: string;
}

export interface CalcListParams {
  page?: number;
  pageSize?: number;
}

// --- LOT 7A : documents (DOCX generation) --------------------------------------

export interface CreateTemplateInput {
  code: string;
  documentType: DocumentType;
  name: string;
  description?: string;
}

export interface CreateTemplateVersionInput {
  /** The .docx uploaded as base64 — the server hashes & stores it (no client-side hashing). */
  docxBase64: string;
  placeholders?: string[];
  configuredMethod?: CalcMethodCode;
  source?: string;
}

export interface GenerateDocumentInput {
  calcRunId: string;
  documentType: DocumentType;
  templateId?: string;
}

/** LOT 8B/8C: scenario-driven generation — the document set is derived from the dossier's scenario. */
export interface GenerateScenarioInput {
  calcRunId: string;
}

export interface DocumentListParams {
  page?: number;
  pageSize?: number;
}

// --- LOT 7B : archival pipeline (PDF/A) ----------------------------------------

/** The downloadable pipeline artifacts (server-produced; the web only fetches bytes). */
export type ArtifactKind = 'DOCX' | 'PDFA' | 'PDFA_REMEDIATED' | 'VERAPDF_REPORT';

/**
 * The surface the UI talks to. A single interface makes the whole client trivially
 * mockable in tests (inject a fake) without spinning up a backend.
 */
export type PhotoPolicyDocumentType =
  | 'PV_BATI'
  | 'PV_VALEUR_REELLE'
  | 'PV_EXPERTISE_COMPLETE'
  | 'RAPPORT_EXPERTISE'
  | 'DOCUMENT_METHODE_SPECIFIQUE';

export interface PhotoRequirementDto {
  documentType: PhotoPolicyDocumentType;
  photosRequired: boolean;
  minimumPhotoCount: number;
}
export interface PhotoPolicyVersionDto {
  id: string;
  version: number;
  status: string;
  note: string | null;
  createdById: string;
  createdAt: string;
  requirements: PhotoRequirementDto[];
}
export interface PhotoPolicySaveInput {
  note?: string;
  requirements: PhotoRequirementDto[];
}

export interface ExifPolicyDto {
  id: string;
  version: number;
  status: string;
  stripAllMetadataOnImport: boolean;
  allowOriginalDownload: boolean;
  unusedRetentionDays: number | null;
  note: string | null;
  createdById: string;
  createdAt: string;
}
export interface ExifPolicySaveInput {
  stripAllMetadataOnImport: boolean;
  allowOriginalDownload: boolean;
  unusedRetentionDays?: number | null;
  note?: string;
}

export interface ApiClient {
  login(input: LoginInput): Promise<AuthTokens>;
  refresh(): Promise<AuthTokens>;
  logout(): Promise<void>;
  me(): Promise<CurrentUser>;

  listMandants(params: MandantListParams): Promise<Page<MandantDto>>;
  getMandant(id: string): Promise<MandantDto>;
  createMandant(input: MandantCreateInput): Promise<MandantDto>;
  updateMandant(id: string, input: MandantUpdateInput): Promise<MandantDto>;
  deactivateMandant(id: string): Promise<MandantDto>;

  listDossiers(params: DossierListParams): Promise<Page<DossierDto>>;
  getDossier(id: string): Promise<DossierDto>;
  createDossier(input: DossierCreateInput, idempotencyKey: string): Promise<DossierDto>;
  updateDossier(id: string, input: DossierUpdateInput): Promise<DossierDto>;
  transitionDossier(id: string, input: DossierTransitionInput): Promise<void>;
  dossierHistory(id: string): Promise<TransitionDto[]>;

  listBiens(dossierId: string, params: BienListParams): Promise<Page<BienDto>>;
  getBien(id: string): Promise<BienDto>;
  createBien(dossierId: string, input: BienCreateInput): Promise<BienDto>;
  updateBien(id: string, input: BienUpdateInput): Promise<BienDto>;

  // LOT 8C — market data (REQ-F-061), dossier-scoped
  listMarketData(dossierId: string, params?: MarketDataListParams): Promise<Page<MarketDataDto>>;
  createMarketData(dossierId: string, input: MarketDataCreateInput): Promise<MarketDataDto>;
  updateMarketData(id: string, input: MarketDataUpdateInput): Promise<MarketDataDto>;
  deleteMarketData(id: string): Promise<{ id: string; deleted: true }>;

  // A-1 — configurable photo-requirement policy (admin)
  getPhotoPolicy(): Promise<PhotoPolicyVersionDto | null>;
  photoPolicyHistory(): Promise<PhotoPolicyVersionDto[]>;
  savePhotoPolicy(input: PhotoPolicySaveInput): Promise<PhotoPolicyVersionDto>;

  // LOT 10E — RGPD/EXIF policy (admin)
  getExifPolicy(): Promise<ExifPolicyDto | null>;
  exifPolicyHistory(): Promise<ExifPolicyDto[]>;
  saveExifPolicy(input: ExifPolicySaveInput): Promise<ExifPolicyDto>;

  listStructures(bienId: string): Promise<StructureDto[]>;
  createStructure(bienId: string, input: StructureCreateInput): Promise<StructureDto>;
  updateStructure(id: string, input: StructureUpdateInput): Promise<StructureDto>;

  listNiveaux(structureId: string): Promise<NiveauDto[]>;
  createNiveau(structureId: string, input: NiveauCreateInput): Promise<NiveauDto>;
  updateNiveau(id: string, input: NiveauUpdateInput): Promise<NiveauDto>;

  listElements(bienId: string): Promise<ElementDto[]>;
  createEspaceElement(niveauId: string, input: EspaceElementCreateInput): Promise<ElementDto>;
  createBienElement(bienId: string, input: BienElementCreateInput): Promise<ElementDto>;
  updateElement(id: string, input: ElementUpdateInput): Promise<ElementDto>;

  listQuantities(elementId: string): Promise<QuantityDto[]>;
  createQuantity(elementId: string, input: QuantityCreateInput): Promise<QuantityDto>;
  /** Server-side computation preview — the web NEVER evaluates a formula locally. */
  previewQuantity(input: QuantityPreviewInput): Promise<QuantityPreview>;
  validateQuantity(id: string): Promise<QuantityDto>;

  listQuantityFormulas(activeOnly?: boolean): Promise<QuantityFormulaDto[]>;

  // --- LOT 5 : référentiels géographiques ---
  listGeoEntities(kind: GeoEntityKind, params?: GeoListParams): Promise<GeoEntityDto[]>;
  createGeoEntity(kind: GeoEntityKind, input: GeoCreateInput): Promise<GeoEntityDto>;
  updateGeoEntity(kind: GeoEntityKind, id: string, input: GeoUpdateInput): Promise<GeoEntityDto>;
  /** POST :id/activate | :id/deactivate — no physical deletion, ever. */
  setGeoEntityActive(kind: GeoEntityKind, id: string, active: boolean): Promise<void>;

  // --- LOT 5 : référentiels techniques ---
  listTechnicalCategories(): Promise<TechnicalCategoryDto[]>;
  createTechnicalCategory(input: CodeNameInput): Promise<TechnicalCategoryDto>;
  listComponentTypes(): Promise<ComponentTypeDto[]>;
  createComponentType(input: ComponentTypeCreateInput): Promise<ComponentTypeDto>;
  addComponentTypeUnit(componentTypeId: string, measurementUnitId: string): Promise<void>;
  removeComponentTypeUnit(componentTypeId: string, unitId: string): Promise<void>;
  listReferentialMeasurementUnits(): Promise<MeasurementUnitRefDto[]>;
  listTariffGroups(): Promise<TariffGroupDto[]>;
  createTariffGroup(input: CodeNameInput): Promise<TariffGroupDto>;
  addTariffGroupMembers(groupId: string, componentTypeIds: string[]): Promise<void>;
  removeTariffGroupMember(groupId: string, componentTypeId: string): Promise<void>;

  // --- LOT 5 : catalogues de coûts ---
  listCostCatalogs(): Promise<CostCatalogDto[]>;
  createCostCatalog(input: CodeNameInput): Promise<CostCatalogDto>;
  listCatalogVersions(catalogId: string): Promise<CatalogVersionDto[]>;
  createCatalogVersion(
    catalogId: string,
    input: CatalogVersionCreateInput,
  ): Promise<CatalogVersionDto>;
  getCatalogVersion(id: string): Promise<CatalogVersionDto>;
  /** Lifecycle: submit / approve / reject — four-eyes enforced by the server (403). */
  transitionCatalogVersion(id: string, action: ReferentialLifecycleAction): Promise<void>;
  listUnitCosts(versionId: string, params?: UnitCostListParams): Promise<UnitCostDto[]>;
  createUnitCost(versionId: string, input: UnitCostCreateInput): Promise<UnitCostDto>;
  updateUnitCost(id: string, input: UnitCostUpdateInput): Promise<UnitCostDto>;
  bulkAssignCosts(versionId: string, input: BulkAssignmentInput): Promise<BulkAssignmentResultDto>;
  previewDuplication(input: DuplicationInput): Promise<DuplicationPreviewDto>;
  applyDuplication(input: DuplicationInput): Promise<DuplicationApplyResultDto>;

  // --- LOT 5 : coefficients ---
  listCoefficientTypes(): Promise<CoefficientTypeDto[]>;
  createCoefficientType(input: CodeNameInput): Promise<CoefficientTypeDto>;
  listCoefficientSets(): Promise<CoefficientSetDto[]>;
  createCoefficientSet(input: CodeNameInput): Promise<CoefficientSetDto>;
  listCoefficientVersions(setId: string): Promise<CoefficientVersionDto[]>;
  createCoefficientVersion(
    setId: string,
    input: CoefficientVersionCreateInput,
  ): Promise<CoefficientVersionDto>;
  getCoefficientVersion(id: string): Promise<CoefficientVersionDto>;
  setCoefficientValue(versionId: string, input: CoefficientValueInput): Promise<void>;
  transitionCoefficientVersion(id: string, action: ReferentialLifecycleAction): Promise<void>;
  resolveCoefficients(params: CoefficientResolutionParams): Promise<CoefficientResolutionDto>;

  // --- LOT 5 : résolution tarifaire (dossier) ---
  previewCostResolution(
    dossierId: string,
    input: CostResolutionInput,
  ): Promise<CostResolutionPreviewDto>;
  confirmCostResolution(
    dossierId: string,
    input: CostResolutionConfirmInput,
  ): Promise<CostSnapshotDto>;

  // --- LOT 5 : import ---
  previewImport(input: ImportInput): Promise<ImportReportDto>;
  commitImport(input: ImportInput): Promise<ImportReportDto>;

  // --- LOT 6B : calculs (dossier-scoped + run-scoped) ---
  /** Freeze the expert's intent; the server resolves & returns the run (PREPARE or BLOQUE). */
  prepareCalculation(dossierId: string, input: CalcPrepareInput): Promise<CalcRunDto>;
  executeCalculation(
    dossierId: string,
    calcRunId: string,
    idempotencyKey?: string,
  ): Promise<CalcRunDto>;
  listCalculations(dossierId: string, params?: CalcListParams): Promise<CalcRunListDto>;
  getCalculation(id: string): Promise<CalcRunDto>;
  getCalculationMethods(id: string): Promise<MethodRunDto[]>;
  reviewCalculation(id: string): Promise<CalcRunDto>;
  validateCalculation(id: string): Promise<CalcRunDto>;
  adjustPublishedValue(id: string, input: CalcAdjustPublishedValueInput): Promise<CalcRunDto>;
  finalizeCalculation(id: string): Promise<CalcRunDto>;
  recalculateCalculation(id: string, input: CalcPrepareInput): Promise<CalcRunDto>;

  // --- LOT 7A : documents (templates + dossier-scoped generation) ---
  listTemplates(): Promise<TemplateDto[]>;
  getTemplate(id: string): Promise<TemplateDto>;
  createTemplate(input: CreateTemplateInput): Promise<TemplateDto>;
  createTemplateVersion(id: string, input: CreateTemplateVersionInput): Promise<TemplateDto>;
  /** Four-eyes activation (server-enforced). */
  activateTemplateVersion(versionId: string): Promise<TemplateDto>;
  /** Submit generation intent; the SERVER builds the manifest & computes the official hash. */
  generateDocument(dossierId: string, input: GenerateDocumentInput): Promise<DocumentDto>;
  /** Scenario-driven generation: the SERVER derives the document set from the dossier's scenario. */
  generateScenario(dossierId: string, input: GenerateScenarioInput): Promise<DocumentDto[]>;
  /** LOT 9A — the PV variants the SERVER offers, with prerequisites, readiness and blocking reasons. */
  listPvVariants(dossierId: string): Promise<PvVariantsDto>;
  listDocuments(dossierId: string, params?: DocumentListParams): Promise<DocumentListDto>;
  listGenerationRuns(dossierId: string): Promise<GenerationRunDto[]>;
  getDocument(id: string): Promise<DocumentDto>;
  /** Read-only server-computed manifest + its official hash. */
  getDocumentManifest(id: string): Promise<DocumentManifestDto>;
  /** Fetch the DOCX and trigger a browser download (Blob) — no client-side rendering. */
  downloadDocument(id: string): Promise<void>;
  /** Finalize — blocked until the SERVER reports pdfaStatus VALID (409 surfaced to the user). */
  finalizeDocument(id: string): Promise<DocumentDto>;

  // --- LOT 7B : archival pipeline (server converts/validates; the web never does) ---
  /** Start the archival PDF/A pipeline (LibreOffice → veraPDF → Ghostscript) on the server. */
  startPipeline(id: string): Promise<StartPipelineDto>;
  /** Relaunch the pipeline after a failed run. */
  retryPipeline(id: string): Promise<StartPipelineDto>;
  /** Read the authoritative pipeline state (status, run, transitions, veraPDF validations). */
  getPipelineStatus(id: string): Promise<PipelineStatusDto>;
  /** Fetch a server-produced artifact and trigger a browser download (Blob) — no client rendering. */
  downloadArtifact(id: string, kind: ArtifactKind): Promise<void>;

  // --- LOT B3 : portail client (lecture seule ; le serveur borne tout au périmètre affecté) ---
  /** Mes dossiers accessibles (affectation active). */
  listPortalDossiers(): Promise<PortalDossierSummary[]>;
  /** Détail d'un de mes dossiers + documents publiés (FINALISE). */
  getPortalDossier(id: string): Promise<PortalDossierDetail>;
  /** Télécharge le PDF validé d'un document autorisé (Blob) — 404 si hors périmètre / non validé. */
  downloadPortalDocument(id: string): Promise<void>;

  // --- Tableau de bord (dashboard) : agrégations LIVE, périmètre borné côté serveur ---
  /** KPIs opérationnels (agrégations Prisma réelles, scopées par le serveur selon case:read_all). */
  getDashboardSummary(): Promise<DashboardSummary>;

  /** Wipe in-memory tokens (used on logout / session expiry). */
  clearSession(): void;
}

type FetchImpl = typeof fetch;

export interface HttpApiClientOptions {
  tokenHolder?: TokenHolder;
  fetchImpl?: FetchImpl;
  /** Called after a failed silent refresh so the app can redirect to /login. */
  onSessionExpired?: () => void;
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    usp.set(key, String(value));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export class HttpApiClient implements ApiClient {
  private readonly tokens: TokenHolder;
  private readonly fetchImpl: FetchImpl;
  private readonly onSessionExpired?: () => void;
  private refreshInFlight: Promise<AuthTokens> | null = null;

  constructor(options: HttpApiClientOptions = {}) {
    this.tokens = options.tokenHolder ?? defaultTokenHolder;
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
    this.onSessionExpired = options.onSessionExpired;
  }

  clearSession(): void {
    this.tokens.clear();
  }

  private async parseError(res: Response): Promise<ApiError> {
    let payload: unknown = null;
    try {
      payload = await res.json();
    } catch {
      // non-JSON error body
    }
    const parsed = ApiErrorBodySchema.safeParse(payload);
    if (parsed.success) {
      return new ApiError(res.status, parsed.data.error);
    }
    return new ApiError(res.status, {
      code: 'UNKNOWN',
      message: `Erreur inattendue (HTTP ${res.status}).`,
    });
  }

  /**
   * Core request. When `auth` is true the Bearer access token is injected and a
   * single silent refresh+retry is attempted on a 401 (expired token).
   */
  private async request<T>(
    method: string,
    path: string,
    opts: {
      body?: unknown;
      auth?: boolean;
      headers?: Record<string, string>;
      parse: (data: unknown) => T;
      allowRefresh?: boolean;
    },
  ): Promise<T> {
    const { body, auth = true, headers = {}, parse, allowRefresh = true } = opts;

    const doFetch = async (): Promise<Response> => {
      const finalHeaders: Record<string, string> = { ...headers };
      if (body !== undefined) finalHeaders['Content-Type'] = 'application/json';
      if (auth) {
        const token = this.tokens.getAccessToken();
        if (token) finalHeaders.Authorization = `Bearer ${token}`;
      }
      return this.fetchImpl(`${API_BASE}${path}`, {
        method,
        headers: finalHeaders,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    };

    let res = await doFetch();

    if (res.status === 401 && auth && allowRefresh && this.tokens.getRefreshToken()) {
      const refreshed = await this.trySilentRefresh();
      if (refreshed) {
        res = await doFetch();
      } else {
        this.tokens.clear();
        this.onSessionExpired?.();
        throw await this.parseError(res);
      }
    }

    if (!res.ok) {
      throw await this.parseError(res);
    }

    if (res.status === 204) {
      return parse(undefined);
    }

    let data: unknown = undefined;
    const text = await res.text();
    if (text) data = JSON.parse(text);
    return parse(data);
  }

  /** Deduplicated single-flight refresh so concurrent 401s share one call. */
  private async trySilentRefresh(): Promise<boolean> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.refresh().finally(() => {
        this.refreshInFlight = null;
      });
    }
    try {
      await this.refreshInFlight;
      return true;
    } catch {
      return false;
    }
  }

  // --- Auth -------------------------------------------------------------------

  async login(input: LoginInput): Promise<AuthTokens> {
    const tokens = await this.request('POST', '/auth/login', {
      body: input,
      auth: false,
      parse: (d) => AuthTokensSchema.parse(d),
    });
    this.tokens.set(tokens);
    return tokens;
  }

  async refresh(): Promise<AuthTokens> {
    const refreshToken = this.tokens.getRefreshToken();
    if (!refreshToken) {
      throw new ApiError(401, { code: 'NO_REFRESH_TOKEN', message: 'Session expirée.' });
    }
    const tokens = await this.request('POST', '/auth/refresh', {
      body: { refreshToken },
      auth: false,
      allowRefresh: false,
      parse: (d) => AuthTokensSchema.parse(d),
    });
    this.tokens.set(tokens);
    return tokens;
  }

  async logout(): Promise<void> {
    const refreshToken = this.tokens.getRefreshToken();
    try {
      if (refreshToken) {
        await this.request('POST', '/auth/logout', {
          body: { refreshToken },
          allowRefresh: false,
          parse: () => undefined,
        });
      }
    } finally {
      this.tokens.clear();
    }
  }

  async me(): Promise<CurrentUser> {
    return this.request('GET', '/auth/me', { parse: (d) => CurrentUserSchema.parse(d) });
  }

  // --- Mandants ---------------------------------------------------------------

  async listMandants(params: MandantListParams): Promise<Page<MandantDto>> {
    const query = buildQuery({
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      type: params.type,
      includeInactive: params.includeInactive,
    });
    return this.request('GET', `/mandants${query}`, {
      parse: (d) => pageSchema(MandantDtoSchema).parse(d),
    });
  }

  async getMandant(id: string): Promise<MandantDto> {
    return this.request('GET', `/mandants/${id}`, { parse: (d) => MandantDtoSchema.parse(d) });
  }

  async createMandant(input: MandantCreateInput): Promise<MandantDto> {
    return this.request('POST', '/mandants', {
      body: input,
      parse: (d) => MandantDtoSchema.parse(d),
    });
  }

  async updateMandant(id: string, input: MandantUpdateInput): Promise<MandantDto> {
    return this.request('PATCH', `/mandants/${id}`, {
      body: input,
      parse: (d) => MandantDtoSchema.parse(d),
    });
  }

  async deactivateMandant(id: string): Promise<MandantDto> {
    return this.request('POST', `/mandants/${id}/deactivate`, {
      parse: (d) => MandantDtoSchema.parse(d),
    });
  }

  // --- Dossiers ---------------------------------------------------------------

  async listDossiers(params: DossierListParams): Promise<Page<DossierDto>> {
    const query = buildQuery({
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      statut: params.statut,
      mandantId: params.mandantId,
      ownerId: params.ownerId,
      sort: params.sort,
    });
    return this.request('GET', `/dossiers${query}`, {
      parse: (d) => pageSchema(DossierDtoSchema).parse(d),
    });
  }

  async getDossier(id: string): Promise<DossierDto> {
    return this.request('GET', `/dossiers/${id}`, { parse: (d) => DossierDtoSchema.parse(d) });
  }

  async createDossier(input: DossierCreateInput, idempotencyKey: string): Promise<DossierDto> {
    return this.request('POST', '/dossiers', {
      body: input,
      headers: { 'Idempotency-Key': idempotencyKey },
      parse: (d) => DossierDtoSchema.parse(d),
    });
  }

  async updateDossier(id: string, input: DossierUpdateInput): Promise<DossierDto> {
    return this.request('PATCH', `/dossiers/${id}`, {
      body: input,
      parse: (d) => DossierDtoSchema.parse(d),
    });
  }

  async transitionDossier(id: string, input: DossierTransitionInput): Promise<void> {
    await this.request('POST', `/dossiers/${id}/transitions`, {
      body: input,
      parse: () => undefined,
    });
  }

  async dossierHistory(id: string): Promise<TransitionDto[]> {
    return this.request('GET', `/dossiers/${id}/history`, {
      parse: (d) => TransitionDtoSchema.array().parse(d),
    });
  }

  // --- LOT 4 : structure immobilière -------------------------------------------

  async listBiens(dossierId: string, params: BienListParams): Promise<Page<BienDto>> {
    const query = buildQuery({ page: params.page, pageSize: params.pageSize });
    return this.request('GET', `/dossiers/${dossierId}/biens${query}`, {
      parse: (d) => pageSchema(BienDtoSchema).parse(d),
    });
  }

  async getBien(id: string): Promise<BienDto> {
    return this.request('GET', `/biens/${id}`, { parse: (d) => BienDtoSchema.parse(d) });
  }

  async createBien(dossierId: string, input: BienCreateInput): Promise<BienDto> {
    return this.request('POST', `/dossiers/${dossierId}/biens`, {
      body: input,
      parse: (d) => BienDtoSchema.parse(d),
    });
  }

  async updateBien(id: string, input: BienUpdateInput): Promise<BienDto> {
    return this.request('PATCH', `/biens/${id}`, {
      body: input,
      parse: (d) => BienDtoSchema.parse(d),
    });
  }

  // --- LOT 8C : market data (REQ-F-061) ----------------------------------------

  async listMarketData(
    dossierId: string,
    params: MarketDataListParams = {},
  ): Promise<Page<MarketDataDto>> {
    const query = buildQuery({ page: params.page, pageSize: params.pageSize });
    return this.request('GET', `/dossiers/${dossierId}/market-data${query}`, {
      parse: (d) => pageSchema(MarketDataDtoSchema).parse(d),
    });
  }

  async createMarketData(dossierId: string, input: MarketDataCreateInput): Promise<MarketDataDto> {
    return this.request('POST', `/dossiers/${dossierId}/market-data`, {
      body: input,
      parse: (d) => MarketDataDtoSchema.parse(d),
    });
  }

  async updateMarketData(id: string, input: MarketDataUpdateInput): Promise<MarketDataDto> {
    return this.request('PATCH', `/market-data/${id}`, {
      body: input,
      parse: (d) => MarketDataDtoSchema.parse(d),
    });
  }

  async deleteMarketData(id: string): Promise<{ id: string; deleted: true }> {
    return this.request('DELETE', `/market-data/${id}`, {
      parse: (d) => z.object({ id: z.string(), deleted: z.literal(true) }).parse(d),
    });
  }

  async getPhotoPolicy(): Promise<PhotoPolicyVersionDto | null> {
    return this.request('GET', '/admin/document-photo-policy', {
      parse: (d) => d as PhotoPolicyVersionDto | null,
    });
  }

  async photoPolicyHistory(): Promise<PhotoPolicyVersionDto[]> {
    return this.request('GET', '/admin/document-photo-policy/history', {
      parse: (d) => d as PhotoPolicyVersionDto[],
    });
  }

  async savePhotoPolicy(input: PhotoPolicySaveInput): Promise<PhotoPolicyVersionDto> {
    return this.request('PUT', '/admin/document-photo-policy', {
      body: input,
      parse: (d) => d as PhotoPolicyVersionDto,
    });
  }

  async getExifPolicy(): Promise<ExifPolicyDto | null> {
    return this.request('GET', '/admin/exif-policy', { parse: (d) => d as ExifPolicyDto | null });
  }

  async exifPolicyHistory(): Promise<ExifPolicyDto[]> {
    return this.request('GET', '/admin/exif-policy/history', {
      parse: (d) => d as ExifPolicyDto[],
    });
  }

  async saveExifPolicy(input: ExifPolicySaveInput): Promise<ExifPolicyDto> {
    return this.request('PUT', '/admin/exif-policy', {
      body: input,
      parse: (d) => d as ExifPolicyDto,
    });
  }

  async listStructures(bienId: string): Promise<StructureDto[]> {
    return this.request('GET', `/biens/${bienId}/structures`, {
      parse: (d) => StructureDtoSchema.array().parse(d),
    });
  }

  async createStructure(bienId: string, input: StructureCreateInput): Promise<StructureDto> {
    return this.request('POST', `/biens/${bienId}/structures`, {
      body: input,
      parse: (d) => StructureDtoSchema.parse(d),
    });
  }

  async updateStructure(id: string, input: StructureUpdateInput): Promise<StructureDto> {
    return this.request('PATCH', `/structures/${id}`, {
      body: input,
      parse: (d) => StructureDtoSchema.parse(d),
    });
  }

  async listNiveaux(structureId: string): Promise<NiveauDto[]> {
    return this.request('GET', `/structures/${structureId}/niveaux`, {
      parse: (d) => NiveauDtoSchema.array().parse(d),
    });
  }

  async createNiveau(structureId: string, input: NiveauCreateInput): Promise<NiveauDto> {
    return this.request('POST', `/structures/${structureId}/niveaux`, {
      body: input,
      parse: (d) => NiveauDtoSchema.parse(d),
    });
  }

  async updateNiveau(id: string, input: NiveauUpdateInput): Promise<NiveauDto> {
    return this.request('PATCH', `/niveaux/${id}`, {
      body: input,
      parse: (d) => NiveauDtoSchema.parse(d),
    });
  }

  async listElements(bienId: string): Promise<ElementDto[]> {
    return this.request('GET', `/biens/${bienId}/elements`, {
      parse: (d) => ElementDtoSchema.array().parse(d),
    });
  }

  async createEspaceElement(
    niveauId: string,
    input: EspaceElementCreateInput,
  ): Promise<ElementDto> {
    return this.request('POST', `/niveaux/${niveauId}/elements`, {
      body: input,
      parse: (d) => ElementDtoSchema.parse(d),
    });
  }

  async createBienElement(bienId: string, input: BienElementCreateInput): Promise<ElementDto> {
    return this.request('POST', `/biens/${bienId}/elements`, {
      body: input,
      parse: (d) => ElementDtoSchema.parse(d),
    });
  }

  async updateElement(id: string, input: ElementUpdateInput): Promise<ElementDto> {
    return this.request('PATCH', `/elements/${id}`, {
      body: input,
      parse: (d) => ElementDtoSchema.parse(d),
    });
  }

  async listQuantities(elementId: string): Promise<QuantityDto[]> {
    return this.request('GET', `/elements/${elementId}/quantities`, {
      parse: (d) => QuantityDtoSchema.array().parse(d),
    });
  }

  async createQuantity(elementId: string, input: QuantityCreateInput): Promise<QuantityDto> {
    return this.request('POST', `/elements/${elementId}/quantities`, {
      body: input,
      parse: (d) => QuantityDtoSchema.parse(d),
    });
  }

  async previewQuantity(input: QuantityPreviewInput): Promise<QuantityPreview> {
    return this.request('POST', '/quantities/preview', {
      body: input,
      parse: (d) => QuantityPreviewSchema.parse(d),
    });
  }

  async validateQuantity(id: string): Promise<QuantityDto> {
    return this.request('POST', `/quantities/${id}/validate`, {
      parse: (d) => QuantityDtoSchema.parse(d),
    });
  }

  async listQuantityFormulas(activeOnly = true): Promise<QuantityFormulaDto[]> {
    const query = buildQuery({ activeOnly });
    return this.request('GET', `/quantity-formulas${query}`, {
      // Defensive: the endpoint may answer with a bare array or a Page envelope.
      parse: (d) => {
        const asArray = z.array(QuantityFormulaDtoSchema).safeParse(d);
        if (asArray.success) return asArray.data;
        return z.object({ items: z.array(QuantityFormulaDtoSchema) }).parse(d).items;
      },
    });
  }

  // --- LOT 5 : référentiels géographiques ---------------------------------------

  async listGeoEntities(kind: GeoEntityKind, params: GeoListParams = {}): Promise<GeoEntityDto[]> {
    const query = buildQuery({
      countryId: params.countryId,
      cityId: params.cityId,
      includeInactive: params.includeInactive ? true : undefined,
    });
    return this.request('GET', `/referentiels/${kind}${query}`, {
      parse: (d) => GeoEntityDtoSchema.array().parse(d),
    });
  }

  async createGeoEntity(kind: GeoEntityKind, input: GeoCreateInput): Promise<GeoEntityDto> {
    return this.request('POST', `/referentiels/${kind}`, {
      body: input,
      parse: (d) => GeoEntityDtoSchema.parse(d),
    });
  }

  async updateGeoEntity(
    kind: GeoEntityKind,
    id: string,
    input: GeoUpdateInput,
  ): Promise<GeoEntityDto> {
    return this.request('PATCH', `/referentiels/${kind}/${id}`, {
      body: input,
      parse: (d) => GeoEntityDtoSchema.parse(d),
    });
  }

  async setGeoEntityActive(kind: GeoEntityKind, id: string, active: boolean): Promise<void> {
    const action = active ? 'activate' : 'deactivate';
    await this.request('POST', `/referentiels/${kind}/${id}/${action}`, {
      parse: () => undefined,
    });
  }

  // --- LOT 5 : référentiels techniques -------------------------------------------

  async listTechnicalCategories(): Promise<TechnicalCategoryDto[]> {
    return this.request('GET', '/referentiels/technical-categories', {
      parse: (d) => TechnicalCategoryDtoSchema.array().parse(d),
    });
  }

  async createTechnicalCategory(input: CodeNameInput): Promise<TechnicalCategoryDto> {
    return this.request('POST', '/referentiels/technical-categories', {
      body: input,
      parse: (d) => TechnicalCategoryDtoSchema.parse(d),
    });
  }

  async listComponentTypes(): Promise<ComponentTypeDto[]> {
    return this.request('GET', '/referentiels/component-types', {
      parse: (d) => ComponentTypeDtoSchema.array().parse(d),
    });
  }

  async createComponentType(input: ComponentTypeCreateInput): Promise<ComponentTypeDto> {
    return this.request('POST', '/referentiels/component-types', {
      body: input,
      parse: (d) => ComponentTypeDtoSchema.parse(d),
    });
  }

  async addComponentTypeUnit(componentTypeId: string, measurementUnitId: string): Promise<void> {
    await this.request('POST', `/referentiels/component-types/${componentTypeId}/units`, {
      body: { measurementUnitId },
      parse: () => undefined,
    });
  }

  async removeComponentTypeUnit(componentTypeId: string, unitId: string): Promise<void> {
    await this.request(
      'POST',
      `/referentiels/component-types/${componentTypeId}/units/${unitId}/remove`,
      { parse: () => undefined },
    );
  }

  async listReferentialMeasurementUnits(): Promise<MeasurementUnitRefDto[]> {
    return this.request('GET', '/referentiels/measurement-units', {
      parse: (d) => MeasurementUnitRefDtoSchema.array().parse(d),
    });
  }

  async listTariffGroups(): Promise<TariffGroupDto[]> {
    return this.request('GET', '/referentiels/tariff-groups', {
      parse: (d) => TariffGroupDtoSchema.array().parse(d),
    });
  }

  async createTariffGroup(input: CodeNameInput): Promise<TariffGroupDto> {
    return this.request('POST', '/referentiels/tariff-groups', {
      body: input,
      parse: (d) => TariffGroupDtoSchema.parse(d),
    });
  }

  async addTariffGroupMembers(groupId: string, componentTypeIds: string[]): Promise<void> {
    await this.request('POST', `/referentiels/tariff-groups/${groupId}/members`, {
      body: { componentTypeIds },
      parse: () => undefined,
    });
  }

  async removeTariffGroupMember(groupId: string, componentTypeId: string): Promise<void> {
    await this.request(
      'POST',
      `/referentiels/tariff-groups/${groupId}/members/${componentTypeId}/remove`,
      { parse: () => undefined },
    );
  }

  // --- LOT 5 : catalogues de coûts ------------------------------------------------

  async listCostCatalogs(): Promise<CostCatalogDto[]> {
    return this.request('GET', '/referentiels/cost-catalogs', {
      parse: (d) => CostCatalogDtoSchema.array().parse(d),
    });
  }

  async createCostCatalog(input: CodeNameInput): Promise<CostCatalogDto> {
    return this.request('POST', '/referentiels/cost-catalogs', {
      body: input,
      parse: (d) => CostCatalogDtoSchema.parse(d),
    });
  }

  async listCatalogVersions(catalogId: string): Promise<CatalogVersionDto[]> {
    return this.request('GET', `/referentiels/cost-catalogs/${catalogId}/versions`, {
      parse: (d) => CatalogVersionDtoSchema.array().parse(d),
    });
  }

  async createCatalogVersion(
    catalogId: string,
    input: CatalogVersionCreateInput,
  ): Promise<CatalogVersionDto> {
    return this.request('POST', `/referentiels/cost-catalogs/${catalogId}/versions`, {
      body: input,
      parse: (d) => CatalogVersionDtoSchema.parse(d),
    });
  }

  async getCatalogVersion(id: string): Promise<CatalogVersionDto> {
    return this.request('GET', `/referentiels/catalog-versions/${id}`, {
      parse: (d) => CatalogVersionDtoSchema.parse(d),
    });
  }

  async transitionCatalogVersion(id: string, action: ReferentialLifecycleAction): Promise<void> {
    await this.request('POST', `/referentiels/catalog-versions/${id}/${action}`, {
      parse: () => undefined,
    });
  }

  async listUnitCosts(versionId: string, params: UnitCostListParams = {}): Promise<UnitCostDto[]> {
    const query = buildQuery({
      scopeType: params.scopeType,
      scopeId: params.scopeId,
      componentTypeId: params.componentTypeId,
    });
    return this.request('GET', `/referentiels/catalog-versions/${versionId}/costs${query}`, {
      parse: (d) => UnitCostDtoSchema.array().parse(d),
    });
  }

  async createUnitCost(versionId: string, input: UnitCostCreateInput): Promise<UnitCostDto> {
    return this.request('POST', `/referentiels/catalog-versions/${versionId}/costs`, {
      body: input,
      parse: (d) => UnitCostDtoSchema.parse(d),
    });
  }

  async updateUnitCost(id: string, input: UnitCostUpdateInput): Promise<UnitCostDto> {
    return this.request('PATCH', `/referentiels/unit-costs/${id}`, {
      body: input,
      parse: (d) => UnitCostDtoSchema.parse(d),
    });
  }

  async bulkAssignCosts(
    versionId: string,
    input: BulkAssignmentInput,
  ): Promise<BulkAssignmentResultDto> {
    return this.request('POST', `/referentiels/catalog-versions/${versionId}/bulk-assignments`, {
      body: input,
      parse: (d) => BulkAssignmentResultDtoSchema.parse(d),
    });
  }

  async previewDuplication(input: DuplicationInput): Promise<DuplicationPreviewDto> {
    return this.request('POST', '/referentiels/duplications/preview', {
      body: input,
      parse: (d) => DuplicationPreviewDtoSchema.parse(d),
    });
  }

  async applyDuplication(input: DuplicationInput): Promise<DuplicationApplyResultDto> {
    return this.request('POST', '/referentiels/duplications/apply', {
      body: input,
      parse: (d) => DuplicationApplyResultDtoSchema.parse(d ?? {}),
    });
  }

  // --- LOT 5 : coefficients -------------------------------------------------------

  async listCoefficientTypes(): Promise<CoefficientTypeDto[]> {
    return this.request('GET', '/referentiels/coefficient-types', {
      parse: (d) => CoefficientTypeDtoSchema.array().parse(d),
    });
  }

  async createCoefficientType(input: CodeNameInput): Promise<CoefficientTypeDto> {
    return this.request('POST', '/referentiels/coefficient-types', {
      body: input,
      parse: (d) => CoefficientTypeDtoSchema.parse(d),
    });
  }

  async listCoefficientSets(): Promise<CoefficientSetDto[]> {
    return this.request('GET', '/referentiels/coefficient-sets', {
      parse: (d) => CoefficientSetDtoSchema.array().parse(d),
    });
  }

  async createCoefficientSet(input: CodeNameInput): Promise<CoefficientSetDto> {
    return this.request('POST', '/referentiels/coefficient-sets', {
      body: input,
      parse: (d) => CoefficientSetDtoSchema.parse(d),
    });
  }

  async listCoefficientVersions(setId: string): Promise<CoefficientVersionDto[]> {
    return this.request('GET', `/referentiels/coefficient-sets/${setId}/versions`, {
      parse: (d) => CoefficientVersionDtoSchema.array().parse(d),
    });
  }

  async createCoefficientVersion(
    setId: string,
    input: CoefficientVersionCreateInput,
  ): Promise<CoefficientVersionDto> {
    return this.request('POST', `/referentiels/coefficient-sets/${setId}/versions`, {
      body: input,
      parse: (d) => CoefficientVersionDtoSchema.parse(d),
    });
  }

  async getCoefficientVersion(id: string): Promise<CoefficientVersionDto> {
    return this.request('GET', `/referentiels/coefficient-versions/${id}`, {
      parse: (d) => CoefficientVersionDtoSchema.parse(d),
    });
  }

  async setCoefficientValue(versionId: string, input: CoefficientValueInput): Promise<void> {
    await this.request('POST', `/referentiels/coefficient-versions/${versionId}/values`, {
      body: input,
      parse: () => undefined,
    });
  }

  async transitionCoefficientVersion(
    id: string,
    action: ReferentialLifecycleAction,
  ): Promise<void> {
    await this.request('POST', `/referentiels/coefficient-versions/${id}/${action}`, {
      parse: () => undefined,
    });
  }

  async resolveCoefficients(
    params: CoefficientResolutionParams,
  ): Promise<CoefficientResolutionDto> {
    const query = buildQuery({ setCode: params.setCode, valuationDate: params.valuationDate });
    return this.request('GET', `/referentiels/coefficient-resolution${query}`, {
      parse: (d) => CoefficientResolutionDtoSchema.parse(d),
    });
  }

  // --- LOT 5 : résolution tarifaire (dossier) --------------------------------------

  async previewCostResolution(
    dossierId: string,
    input: CostResolutionInput,
  ): Promise<CostResolutionPreviewDto> {
    return this.request('POST', `/dossiers/${dossierId}/cost-resolution/preview`, {
      body: input,
      parse: (d) => CostResolutionPreviewDtoSchema.parse(d),
    });
  }

  async confirmCostResolution(
    dossierId: string,
    input: CostResolutionConfirmInput,
  ): Promise<CostSnapshotDto> {
    return this.request('POST', `/dossiers/${dossierId}/cost-resolution/confirm`, {
      body: input,
      parse: (d) => CostSnapshotDtoSchema.parse(d),
    });
  }

  // --- LOT 5 : import ---------------------------------------------------------------

  async previewImport(input: ImportInput): Promise<ImportReportDto> {
    return this.request('POST', '/referentiels/imports/preview', {
      body: input,
      parse: (d) => ImportReportDtoSchema.parse(d),
    });
  }

  async commitImport(input: ImportInput): Promise<ImportReportDto> {
    return this.request('POST', '/referentiels/imports/commit', {
      body: input,
      parse: (d) => ImportReportDtoSchema.parse(d),
    });
  }

  // --- LOT 6B : calculs -------------------------------------------------------------

  async prepareCalculation(dossierId: string, input: CalcPrepareInput): Promise<CalcRunDto> {
    return this.request('POST', `/dossiers/${dossierId}/calculations/prepare`, {
      body: input,
      parse: (d) => CalcRunDtoSchema.parse(d),
    });
  }

  async executeCalculation(
    dossierId: string,
    calcRunId: string,
    idempotencyKey?: string,
  ): Promise<CalcRunDto> {
    return this.request('POST', `/dossiers/${dossierId}/calculations/execute`, {
      body: { calcRunId },
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
      parse: (d) => CalcRunDtoSchema.parse(d),
    });
  }

  async listCalculations(dossierId: string, params: CalcListParams = {}): Promise<CalcRunListDto> {
    const query = buildQuery({ page: params.page, pageSize: params.pageSize });
    return this.request('GET', `/dossiers/${dossierId}/calculations${query}`, {
      parse: (d) => CalcRunListDtoSchema.parse(d),
    });
  }

  async getCalculation(id: string): Promise<CalcRunDto> {
    return this.request('GET', `/calculations/${id}`, {
      parse: (d) => CalcRunDtoSchema.parse(d),
    });
  }

  async getCalculationMethods(id: string): Promise<MethodRunDto[]> {
    return this.request('GET', `/calculations/${id}/methods`, {
      parse: (d) => MethodRunDtoSchema.array().parse(d),
    });
  }

  async reviewCalculation(id: string): Promise<CalcRunDto> {
    return this.request('POST', `/calculations/${id}/review`, {
      body: {},
      parse: (d) => CalcRunDtoSchema.parse(d),
    });
  }

  async validateCalculation(id: string): Promise<CalcRunDto> {
    return this.request('POST', `/calculations/${id}/validate`, {
      body: {},
      parse: (d) => CalcRunDtoSchema.parse(d),
    });
  }

  async adjustPublishedValue(
    id: string,
    input: CalcAdjustPublishedValueInput,
  ): Promise<CalcRunDto> {
    return this.request('POST', `/calculations/${id}/published-value-adjustment`, {
      body: input,
      parse: (d) => CalcRunDtoSchema.parse(d),
    });
  }

  async finalizeCalculation(id: string): Promise<CalcRunDto> {
    return this.request('POST', `/calculations/${id}/finalize`, {
      body: {},
      parse: (d) => CalcRunDtoSchema.parse(d),
    });
  }

  async recalculateCalculation(id: string, input: CalcPrepareInput): Promise<CalcRunDto> {
    return this.request('POST', `/calculations/${id}/recalculate`, {
      body: input,
      parse: (d) => CalcRunDtoSchema.parse(d),
    });
  }

  // --- LOT 7A : documents -----------------------------------------------------------

  async listTemplates(): Promise<TemplateDto[]> {
    return this.request('GET', '/document-templates', {
      parse: (d) => TemplateDtoSchema.array().parse(d),
    });
  }

  async getTemplate(id: string): Promise<TemplateDto> {
    return this.request('GET', `/document-templates/${id}`, {
      parse: (d) => TemplateDtoSchema.parse(d),
    });
  }

  async createTemplate(input: CreateTemplateInput): Promise<TemplateDto> {
    return this.request('POST', '/document-templates', {
      body: input,
      parse: (d) => TemplateDtoSchema.parse(d),
    });
  }

  async createTemplateVersion(id: string, input: CreateTemplateVersionInput): Promise<TemplateDto> {
    return this.request('POST', `/document-templates/${id}/versions`, {
      body: input,
      parse: (d) => TemplateDtoSchema.parse(d),
    });
  }

  async activateTemplateVersion(versionId: string): Promise<TemplateDto> {
    return this.request('POST', `/document-template-versions/${versionId}/activate`, {
      body: {},
      parse: (d) => TemplateDtoSchema.parse(d),
    });
  }

  async generateDocument(dossierId: string, input: GenerateDocumentInput): Promise<DocumentDto> {
    return this.request('POST', `/dossiers/${dossierId}/documents/generate`, {
      body: input,
      parse: (d) => DocumentDtoSchema.parse(d),
    });
  }

  async generateScenario(dossierId: string, input: GenerateScenarioInput): Promise<DocumentDto[]> {
    return this.request('POST', `/dossiers/${dossierId}/documents/generate-scenario`, {
      body: input,
      parse: (d) => DocumentDtoSchema.array().parse(d),
    });
  }

  async listPvVariants(dossierId: string): Promise<PvVariantsDto> {
    return this.request('GET', `/dossiers/${dossierId}/documents/pv-variants`, {
      parse: (d) => PvVariantsDtoSchema.parse(d),
    });
  }

  async listDocuments(
    dossierId: string,
    params: DocumentListParams = {},
  ): Promise<DocumentListDto> {
    const query = buildQuery({ page: params.page, pageSize: params.pageSize });
    return this.request('GET', `/dossiers/${dossierId}/documents${query}`, {
      parse: (d) => DocumentListDtoSchema.parse(d),
    });
  }

  async listGenerationRuns(dossierId: string): Promise<GenerationRunDto[]> {
    return this.request('GET', `/dossiers/${dossierId}/documents/generation-runs`, {
      parse: (d) => GenerationRunDtoSchema.array().parse(d),
    });
  }

  async getDocument(id: string): Promise<DocumentDto> {
    return this.request('GET', `/documents/${id}`, {
      parse: (d) => DocumentDtoSchema.parse(d),
    });
  }

  async getDocumentManifest(id: string): Promise<DocumentManifestDto> {
    return this.request('GET', `/documents/${id}/manifest`, {
      parse: (d) => DocumentManifestDtoSchema.parse(d),
    });
  }

  async downloadDocument(id: string): Promise<void> {
    const token = this.tokens.getAccessToken();
    const res = await this.fetchImpl(`${API_BASE}/documents/${id}/download`, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw await this.parseError(res);
    const blob = await res.blob();
    // Derive a filename from the Content-Disposition header, falling back to the id.
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const filename = match?.[1] ?? `document-${id}.docx`;
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  async finalizeDocument(id: string): Promise<DocumentDto> {
    return this.request('POST', `/documents/${id}/finalize`, {
      body: {},
      parse: (d) => DocumentDtoSchema.parse(d),
    });
  }

  // --- LOT 7B : archival pipeline -----------------------------------------------

  async startPipeline(id: string): Promise<StartPipelineDto> {
    return this.request('POST', `/documents/${id}/pipeline/start`, {
      body: {},
      parse: (d) => StartPipelineDtoSchema.parse(d),
    });
  }

  async retryPipeline(id: string): Promise<StartPipelineDto> {
    return this.request('POST', `/documents/${id}/pipeline/retry`, {
      body: {},
      parse: (d) => StartPipelineDtoSchema.parse(d),
    });
  }

  async getPipelineStatus(id: string): Promise<PipelineStatusDto> {
    return this.request('GET', `/documents/${id}/pipeline`, {
      parse: (d) => PipelineStatusDtoSchema.parse(d),
    });
  }

  async downloadArtifact(id: string, kind: ArtifactKind): Promise<void> {
    const token = this.tokens.getAccessToken();
    const res = await this.fetchImpl(`${API_BASE}/documents/${id}/artifacts/${kind}/download`, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw await this.parseError(res);
    const blob = await res.blob();
    // Derive a filename from the Content-Disposition header, falling back to the id + kind.
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const filename = match?.[1] ?? `document-${id}-${kind}`;
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  // --- LOT B3 : portail client -------------------------------------------------

  async listPortalDossiers(): Promise<PortalDossierSummary[]> {
    return this.request('GET', '/portal/dossiers', {
      parse: (d) => z.array(PortalDossierSummarySchema).parse(d),
    });
  }

  async getPortalDossier(id: string): Promise<PortalDossierDetail> {
    return this.request('GET', `/portal/dossiers/${id}`, {
      parse: (d) => PortalDossierDetailSchema.parse(d),
    });
  }

  async getDashboardSummary(): Promise<DashboardSummary> {
    return this.request('GET', '/dashboard/summary', {
      parse: (d) => DashboardSummarySchema.parse(d),
    });
  }

  async downloadPortalDocument(id: string): Promise<void> {
    const token = this.tokens.getAccessToken();
    const res = await this.fetchImpl(`${API_BASE}/portal/documents/${id}/download`, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw await this.parseError(res);
    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const filename = match?.[1] ?? `document-${id}.pdf`;
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }
}

/** Convenience factory used by the app entrypoint. */
export function createApiClient(options: HttpApiClientOptions = {}): ApiClient {
  return new HttpApiClient(options);
}

export { createTokenHolder };
